// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    ITextFieldStyles,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    TextField,
} from 'office-ui-fabric-react';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';

import ErrorMessage from './../../components/ErrorMessage';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import { validateEmail, validatePhoneNumber } from 'src/helpers/validation';
import {
    getRegisterInfoData, selectedShippingOfferSelector, GET_REGISTER_INFO_DATA,
    reActivateOrderStatusSelector, isOnSiteRepairStatusSelector
} from './../../store/orderCreation';
import { shippingAddressSelector, orderCreationSelector, customerServiceLocationAddressSelector, isSoftwareOrderSelector } from './../../store/orderCreation/selectors';
import {
    deviceSelector,
} from './../../store/deviceInfo/selectors';
import { LoadingState } from './../../store/actionStatus';

import Styles from './registerDevice.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { getUserTierConfig, checkIsUserEnterpriseCategoryFromTier } from './../../lib/userTierConfig';
import { userAgentTierSelector } from './../../store/user';

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        marginLeft: 0,
        width: 320,
        marginRight: 32,
    },
};

interface CustomerInfoType {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    microsoftAccount: string;
    orgName: string;
}

interface PropType {
    errorMessage?: string;
    editedInfo?: CustomerInfoType;
    displayOnlyOperationId?: boolean;
}

const INITIAL_STATE: CustomerInfoType = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    microsoftAccount: '',
    orgName: '',
};

export default function RegisterDevice(props: PropType) {
    const selectedShippingOffers = useSelector(state => selectedShippingOfferSelector(state));
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));
    const isSoftwareOrder = useSelector(state => isSoftwareOrderSelector(state));
    const customerServiceLocationAddress = useSelector(state => customerServiceLocationAddressSelector(state));
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [customerInfo, setCustomerInfo] = React.useState<CustomerInfoType>(props.editedInfo || INITIAL_STATE);
    const [isChecked, setIsChecked] = React.useState(false);
    const [validation, setValidation] = React.useState({ phoneNumber: undefined, email: undefined, orgName: undefined, firstName: undefined, lastName: undefined });
    const handleChange = (name: string) => (event: any) => {
        setIsChecked(false);
        setCustomerInfo({ ...customerInfo, [name]: event.target.value });
    };
    const deviceInfo = useSelector(state => deviceSelector(state));
    const shippingAddress = useSelector(state => shippingAddressSelector(state));
    const orderInfo = useSelector(state => orderCreationSelector(state));

    //to get the current tier information and check whether Enterprise category or not
    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    const isEnterpriseCustomer = checkIsUserEnterpriseCategoryFromTier(userAgentTier);

    const callStatus = useSelector(state =>
        statusSelector(state)(GET_REGISTER_INFO_DATA)
    );
    const isLoading = callStatus.status === LoadingState.started;
    const onSubmit = () => {
        const inputFields = customerInfo;
        Object.keys(inputFields).map(inputVal => inputFields[inputVal] = typeof inputFields[inputVal] === 'string' ? inputFields[inputVal].trim() : inputFields[inputVal]);
        setCustomerInfo((prevState: any) => ({ ...prevState, inputFields }));
        if (!validateInput()) {
            return;
        }

        logPageAction('Register user button clicked');

        const serialNumber = deviceInfo.serialNumber;
        const isAccessoryRepairOrder = (orderInfo.repairItems.accessories && Object.keys(orderInfo.repairItems.accessories).length > 0);
        dispatch(getRegisterInfoData({
            ...customerInfo,
            serialNumber,
            isAccessoryRepairOrder,
        }));
    };

    const validateInput = () => {
        let isValid: boolean = true;
        let validationMessage = { email: undefined, phoneNumber: undefined, firstName: undefined, lastName: undefined, orgName: undefined };

        if (!customerInfo.firstName) {
            validationMessage = { ...validationMessage, firstName: t('required_message', { fieldName: isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name') }) };
            isValid = false;
        }

        if (!customerInfo.lastName) {
            validationMessage = { ...validationMessage, lastName: t('required_message', { fieldName: isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name') }) };
            isValid = false;
        }

        if (userConfig.commercialRegistration && isEnterpriseCustomer === false) {
            if (!customerInfo.orgName) {
                validationMessage = { ...validationMessage, orgName: t('required_message', { fieldName: t('organization_name') }) };
                isValid = false;
            }
        }

        if (!customerInfo.phoneNumber) {
            validationMessage = { ...validationMessage, phoneNumber: t('required_message', { fieldName: t('phone_number') }) };
            isValid = false;
        }

        else if (!validatePhoneNumber(customerInfo.phoneNumber)) {
            validationMessage = { ...validationMessage, phoneNumber: t('invalid_phone_number') };
            isValid = false;
        }

        if (!customerInfo.microsoftAccount) {
            validationMessage = { ...validationMessage, email: t('required_message', { fieldName: t('email') }) };
            isValid = false;
        }

        else if (!validateEmail(customerInfo.microsoftAccount)) {
            validationMessage = { ...validationMessage, email: t('invalid_email_address') };
            isValid = false;
        }
        setValidation(validationMessage);
        return isValid;
    };

    const checkInput = (e: any) => {
        const re = /[0-9+]+/g;
        if (!re.test(e.key)) {
            e.preventDefault();
        }
    };
    const checkboxStyles = () => {
        return {
            root: {
                marginTop: '10px',
            },
        };
    };
    const userConfig = getUserTierConfig();

    const onCheckboxChange = (event: any) => {
        setIsChecked(event.target.checked);

        if (event.target.checked) {
            if (isOnSiteRepair) {
                logPageAction('Use customer service location address in onsite repair');
                const { firstName, lastName, phoneNumber, email } = customerServiceLocationAddress;

                setCustomerInfo({
                    ...customerInfo,
                    phoneNumber,
                    firstName,
                    lastName,
                    microsoftAccount: email,
                });
            }
            else {
                logPageAction('Use shipping address selected');
                const { firstName, lastName, phoneNumber, email } = shippingAddress;

                setCustomerInfo({
                    ...customerInfo,
                    phoneNumber,
                    firstName,
                    lastName,
                    microsoftAccount: email,
                });
            }
        } else {
            logPageAction('Use customer location address/shipping address unselected');
            setCustomerInfo({ ...customerInfo, firstName: '', lastName: '', phoneNumber: '', microsoftAccount: '', orgName: '' });
        }
    };
    return (
        <section id="registration">
            <div className={clsx('ms-Grid')}>
                <div className={clsx(Styles.registerDevice)}>
                    <div className="ms-Grid-row">
                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            <h2 className={Styles.titleHeading}>
                                {t('register_device')}
                            </h2>
                        </div>
                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            {Object.entries(selectedShippingOffers).length !==
                                0 &&
                                selectedShippingOffers.constructor === Object ? (
                                !reActivateOrder &&
                                <Checkbox
                                    checked={isChecked}
                                    id="chkSameShippingAddress"
                                    label={isOnSiteRepair ? (t('use_service_location_address')) : (isSoftwareOrder ? t('use_customer_details_data') : t('use_shipping_address_data'))}
                                    onChange={onCheckboxChange}
                                    styles={checkboxStyles}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className="ms-Grid-row">
                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            <TextField
                                ariaLabel={isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                                autoFocus={true}
                                disabled={isLoading}
                                label={isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                                onChange={handleChange('firstName')}
                                required={true}
                                styles={textFieldStyles}
                                value={customerInfo.firstName}
                                errorMessage={validation.firstName}
                            />
                        </div>
                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            <TextField
                                ariaLabel={isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                                disabled={isLoading}
                                label={isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                                onChange={handleChange('lastName')}
                                required={true}
                                styles={textFieldStyles}
                                value={customerInfo.lastName}
                                errorMessage={validation.lastName}
                            />
                        </div>


                        {userConfig.commercialRegistration && isEnterpriseCustomer === false &&
                            <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                                <TextField
                                    ariaLabel={t('organization_name')}
                                    disabled={isLoading}
                                    label={t('organization_name')}
                                    onChange={handleChange('orgName')}
                                    required={true}
                                    styles={textFieldStyles}
                                    value={customerInfo.orgName}
                                    errorMessage={validation.orgName}
                                />
                            </div>
                        }
                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            <TextField
                                ariaLabel={t('email')}
                                disabled={isLoading}
                                errorMessage={validation.email}
                                label={t('email')}
                                name="email"
                                onChange={handleChange('microsoftAccount')}
                                required={true}
                                styles={textFieldStyles}
                                value={customerInfo.microsoftAccount}
                            />
                        </div>


                        <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                            <TextField
                                ariaLabel={t('phone_number')}
                                disabled={isLoading}
                                errorMessage={validation.phoneNumber}
                                label={t('phone_number')}
                                onChange={handleChange('phoneNumber')}
                                onKeyPress={checkInput}
                                required={true}
                                styles={textFieldStyles}
                                value={customerInfo.phoneNumber}
                            />
                        </div>
                    </div>
                    <div className="ms-Grid-row">
                        <div className={clsx('ms-Grid-col', 'ms-sm9')} />
                        <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                            <div className={Styles.buttonContainer}>
                                <div
                                    className={Styles.buttonAndSpinnerContainer}
                                >
                                    <PrimaryButton
                                        allowDisabledFocus={true}
                                        ariaLabel={t('register_user')}
                                        className={Styles.submit}
                                        disabled={isLoading}
                                        onClick={onSubmit}
                                        text={t('register_user')}
                                    />
                                    {isLoading && (
                                        <Spinner
                                            className={Styles.spinner}
                                            size={SpinnerSize.medium}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="ms-Grid-row">
                        <ErrorMessage
                            contactSupport={!!props.errorMessage}
                            message={props.errorMessage || ''}
                            displayOnlyOperationId={props.displayOnlyOperationId}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
